import {
    ActionIcon,
    Alert,
    Badge,
    Blockquote,
    Button,
    Group,
    Paper,
    Popover,
    Select,
    Skeleton,
    Text,
    Title,
    Tooltip,
    TypographyStylesProvider,
} from "@mantine/core";
import { IconAnalyze, IconClipboardList, IconDownload, IconExclamationCircle, IconInfoCircle, IconLanguage } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { Suspense, useEffect, useState } from "react";
import { httpPostGraphql } from "shared/api/httpClient";
import { Content } from "shared/components/global/Content";
import { FileItem } from "shared/components/global/FileItem";
import { MessageNotes } from "shared/components/message/MessageNotes";
import { MessagePoll } from "shared/components/message/MessagePoll";
import { MessageStatusBadge } from "shared/components/message/MessageStatusBadge";
import { useUser } from "shared/graphql/users";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { usePermissionCheck } from "shared/stores/oidc";
import { arr } from "shared/utils/array";
import { catching } from "shared/utils/fns";
import { titlecase } from "shared/utils/fns";
import { mdToHtml } from "shared/utils/md";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { Language, MessageSeverity, MessageStatus, Permission } from "src/gql/graphql";
import { queryClient } from "src/queryClient";

const Component = () => {
    const { id } = useParams({ from: "/_auth/messages/$id/" });
    const { user } = useUser();
    const {
        data: { message },
    } = useSuspenseQuery(query(id));
    const canViewInsights = usePermissionCheck(Permission.GroupAnalyticsView, message.sender.group.id);
    const canViewDetails = usePermissionCheck(Permission.GroupMessageSendFrom, message.sender.group.id);
    const hasActiveBanner = message.status == MessageStatus.Published && canViewDetails;

    const [language, setLanguage] = useState(user?.language ?? Language.English);
    const locale = message.locales.find((it) => it.language == language) ?? message;

    const [innerBody, setInnerBody] = useState("");

    const acknowledge = useGraphqlMutation(muts.acknowledgeByMessage);

    useEffect(() => {
        void mdToHtml(locale.richBody).then((safeHtml) => setInnerBody(safeHtml));
    }, [locale.richBody]);

    const languages = message.locales.map((each) => each.language);

    return (
        <>
            <Content paper actionWrap p="sm">
                <Content.Heading backable wrap="nowrap">
                    <Title order={3} lineClamp={1}>
                        {locale.subject}
                    </Title>
                    <MessageStatusBadge status={message.status} />
                    {message.severity == MessageSeverity.Urgent && (
                        <Badge miw="fit-content" color="yellow">
                            Urgent
                        </Badge>
                    )}
                </Content.Heading>

                {hasActiveBanner && (
                    <Content.Above>
                        <Paper>
                            <Alert
                                variant="light"
                                color="green"
                                title="Active Message"
                                icon={<IconInfoCircle size={16} />}
                                mb="xs"
                                styles={{ body: { gap: "0" } }}
                            >
                                This message is active until {new Date(parseInt(message.expireAt!!)).toLocaleString()}
                            </Alert>
                        </Paper>
                    </Content.Above>
                )}

                {canViewDetails && (
                    <Content.Action>
                        <Link to="/messages/$id/details" params={{ id }}>
                            <Button variant="transparent" leftSection={<IconClipboardList size={16} />}>
                                Details
                            </Button>
                        </Link>
                    </Content.Action>
                )}
                {canViewInsights && (
                    <Content.Action>
                        <Link to="/messages/$id/insights" params={{ id }}>
                            <Button variant="transparent" leftSection={<IconAnalyze size={16} />}>
                                Insights
                            </Button>
                        </Link>
                    </Content.Action>
                )}
                <Content.Action>
                    <Popover trapFocus position="bottom" shadow="md" withArrow closeOnClickOutside={false}>
                        <Popover.Target>
                            <Button leftSection={<IconLanguage size={16} />} variant="transparent">
                                Language
                            </Button>
                        </Popover.Target>
                        <Popover.Dropdown>
                            <Select
                                label="Message Language"
                                variant="filled"
                                description="Select the language to view the message in"
                                data={languages.map((value) => ({
                                    value,
                                    label: titlecase(value) + (value == message.publishedBy?.language ? " (original composed)" : ""),
                                }))}
                                defaultValue={language}
                                renderOption={({ option }) => (
                                    <Text size="sm">
                                        {titlecase(option.value)}
                                        {option.value == message.publishedBy?.language && <em> (original composed)</em>}
                                    </Text>
                                )}
                                onChange={(_, { value }) => setLanguage(value as Language)}
                                wrapperProps={{ style: { width: "250px" } }}
                            />
                        </Popover.Dropdown>
                    </Popover>
                </Content.Action>

                <div>
                    <Title order={3}>{locale.subject}</Title>
                    <Text size="xs" c="dimmed">{`Sent: ${new Date(+message.publishedAt!).toLocaleString()}`}</Text>
                    <Text size="xs" c="dimmed">{`From: ${message.sender.name}`}</Text>
                </div>

                <article style={{ wordBreak: "break-word" }}>
                    {catching(() => (
                        <TypographyStylesProvider>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: innerBody,
                                }}
                            />
                        </TypographyStylesProvider>
                    )) ?? (
                        <Blockquote
                            m="md"
                            icon={
                                <Tooltip label="Viewing raw message content">
                                    <IconExclamationCircle size={16} />
                                </Tooltip>
                            }
                            variant="warning"
                            cite="Failed to parse message body"
                        >
                            {locale.richBody}
                        </Blockquote>
                    )}
                </article>

                <Group>
                    <Suspense fallback={arr.make(2, <Skeleton height={100} width={250} />)}>
                        {message.files.map(({ id, preSigned }) => (
                            <FileItem fileId={id} key={id}>
                                <FileItem.Action>
                                    <ActionIcon
                                        component="a"
                                        variant="transparent"
                                        size="xs"
                                        href={`${import.meta.env["VITE_API_BASE_URL"]}/presignedUrl/${preSigned.id}/download`}
                                        aria-label="Download attachment"
                                    >
                                        <IconDownload size={16} />
                                    </ActionIcon>
                                </FileItem.Action>
                            </FileItem>
                        ))}
                    </Suspense>
                </Group>
                {!message.poll && message.acknowledgable && (
                    <Button
                        color="red"
                        disabled={
                            !!message.about?.didAck ||
                            !message.about?.canAck ||
                            acknowledge.isPending ||
                            message.status == MessageStatus.Expired
                        }
                        loading={acknowledge.isPending}
                        onClick={() => {
                            void acknowledge
                                .mutateAsync({ messageId: message.id })
                                .then(() => {
                                    notify.show.success("Acknowledged");
                                    return qk.invalidate("messages", "detail", id);
                                })
                                .catch(notify.catch);
                        }}
                    >
                        Acknowledge
                    </Button>
                )}
            </Content>

            <Suspense fallback={<Skeleton height={100} width="100%" />}>
                <MessagePoll msgId={message.id} />
            </Suspense>
            <Content>
                <Suspense fallback={<Skeleton height={100} width="100%" />}>
                    <MessageNotes language={language} msgId={message.id} />
                </Suspense>
            </Content>
        </>
    );
};

const query = (id: string) =>
    queryOptions({
        queryKey: qk("messages", "detail", id),
        queryFn: () =>
            httpPostGraphql(
                graphql(`
                    query MessageDetail($id: UUID!) {
                        message(id: $id) {
                            id
                            status
                            subject
                            richBody
                            expireAt
                            locales {
                                language
                                subject
                                richBody
                            }
                            publishedBy {
                                language
                            }
                            sender {
                                group {
                                    id
                                }
                                name
                            }
                            publishedAt
                            files {
                                id
                                preSigned {
                                    id
                                }
                            }
                            acknowledgable
                            about {
                                canAck
                                didAck
                            }
                            poll {
                                id
                            }
                            severity
                        }
                    }
                `),
                { id },
            ),
    });

const muts = {
    acknowledgeByMessage: graphql(`
        mutation MessageDetailAcknowledge($messageId: UUID!) {
            acknowledgeByMessage(messageId: $messageId)
        }
    `),
};

/** @public */
export const Route = createFileRoute("/_auth/messages/$id/")({
    component: Component,
    loader: ({ params: { id } }) => queryClient.ensureQueryData(query(id)),
});
